/**
 * Manifest write-back — on `skaile library add`, when the upstream repo has
 * no `skaile.manifest.yaml`, run discovery and write a CLI-generated manifest.
 *
 * Behaviour by ownership:
 *   - owner:       write file, leave to commit upstream (CLI nudges)
 *   - contributor: write file, register in .git/info/exclude (silent)
 *   - reader:      same as contributor
 *
 * @docLink packages/library/concepts#manifest-writeback
 */

import * as fs from "node:fs";
import * as path from "node:path";
import {
  type AssetEntry,
  encodeSkaileManifest,
  type SkPublishManifest,
} from "@skaile/workspaces/core";
import type { DiscoveredAsset } from "@skaile/workspaces/discovery";
import type { LibraryOwnership, LibraryStructure } from "../user-library.js";

export interface WriteManifestInput {
  libraryPath: string;
  ownership: LibraryOwnership;
  structure: LibraryStructure;
}

export interface WriteManifestResult {
  wrote: boolean;
  manifestPath: string;
}

const MANIFEST_FILENAME = "skaile.manifest.yaml";

export async function writeManifestIfMissing(
  input: WriteManifestInput,
): Promise<WriteManifestResult> {
  const manifestPath = path.join(input.libraryPath, MANIFEST_FILENAME);
  if (fs.existsSync(manifestPath)) return { wrote: false, manifestPath };

  // Minimal `skaile.manifest.yaml` (SkPublishManifest) stub — author can fill in
  // discovery results later. The full discovery integration (calling
  // @skaile/workspaces/discovery's discoverAssetsInTree) is deferred to the CLI
  // command, which already imports @skaile/workspaces/discovery.
  const now = new Date().toISOString();
  const yaml = `# Generated by skaile library add on ${now} (structure: ${input.structure})
# Edit by hand to inline discovered assets; or run \`skaile library refresh-manifest\`.
publisher: ""
assets: []
`;
  fs.writeFileSync(manifestPath, yaml, "utf-8");

  // For non-owners, hide from upstream so the user doesn't accidentally PR it.
  if (input.ownership !== "owner") {
    const excludePath = path.join(input.libraryPath, ".git", "info", "exclude");
    if (fs.existsSync(path.dirname(excludePath))) {
      const current = fs.existsSync(excludePath) ? fs.readFileSync(excludePath, "utf-8") : "";
      if (!current.includes(MANIFEST_FILENAME)) {
        fs.writeFileSync(
          excludePath,
          (current.endsWith("\n") || current === "" ? current : `${current}\n`) +
            `# skaile-managed local-overlay manifest\n${MANIFEST_FILENAME}\n`,
          "utf-8",
        );
      }
    }
  }

  return { wrote: true, manifestPath };
}

// ---------------------------------------------------------------------------
// Curated manifest writer (sidecar authoring — `skaile source manifest init`)
// ---------------------------------------------------------------------------

export interface WriteCuratedManifestInput {
  /** Absolute path to the target manifest file (e.g. `<store>/manifests/<slug>.yaml`). */
  manifestFile: string;
  /** Repo-level publisher (bare, no leading `@`) inherited by every asset. */
  publisher: string;
  /** Assets to inventory, as returned by `discoverAssetsInTree`. */
  assets: DiscoveredAsset[];
  /** Overwrite an existing file. Default `false` (idempotent — never clobbers). */
  force?: boolean;
  /**
   * When `true`, EVERY asset inherits the top-level `publisher` — no per-asset
   * `publisher:` is stamped. Set by `source manifest init`: on the glob path each
   * `DiscoveredAsset.publisher` is the bare first path segment (`scripts`/`skills`),
   * so the default divergence-stamp would clobber the intended org/`--publisher`
   * with junk per-asset overrides. Default `false` (stamp on divergence).
   */
  inheritPublisher?: boolean;
}

export interface WriteCuratedManifestResult {
  wrote: boolean;
  manifestPath: string;
  /** Number of assets written (0 when `wrote` is false). */
  assetCount: number;
}

/**
 * Map one discovered asset to a manifest `assets[]` entry. Uses the asset's
 * directory as `root:` (recursive-walk mode — the manifest dispatcher re-walks
 * it), inherits the repo-level publisher unless the asset's own publisher
 * differs, and carries over the store-listing description.
 *
 * A `root:`-mode entry deliberately OMITS `sha256`. `DiscoveredAsset.sha256` is
 * computed over discovery's FILTERED per-kind file set, but `source add`
 * re-walks the WHOLE `root` dir → a different file set → a different composite
 * hash → `ManifestHashMismatch`, which fails the asset (0 indexed). A missing
 * sha256 is a non-fatal advisory (the re-walked hash is used), so leaving it
 * off lets the asset index cleanly. Only granular `files: [{path, sha256}]`
 * entries carry an author-pinned hash — this writer never produces those.
 */
function discoveredToAssetEntry(
  asset: DiscoveredAsset,
  repoPublisher: string,
  inheritPublisher: boolean,
): AssetEntry {
  const root = path.posix.dirname(asset.relativePath.replace(/\\/g, "/"));
  const entry: AssetEntry = {
    kind: asset.kind,
    name: asset.name,
    root: root === "." ? asset.name : root,
    version: asset.version,
  };
  // Stamp a per-asset publisher only when it diverges from the repo default AND
  // the caller hasn't asked every asset to inherit the top-level publisher.
  if (!inheritPublisher && asset.publisher && asset.publisher !== repoPublisher)
    entry.publisher = asset.publisher;
  const deps = asset.requires.map((r) => r.to).filter((t) => t.length > 0);
  if (deps.length > 0) entry.dependencies = deps;
  if (typeof asset.manifest.description === "string")
    entry.description = asset.manifest.description;
  return entry;
}

/**
 * Serialize a curated `skaile.manifest.yaml` (`SkPublishManifest` with an
 * `assets:` inventory) from a discovery run to an explicit file path. Used by
 * `skaile source manifest init` to author a manifest OVER a source the user
 * does not own — the file is stored outside that source, under the local store
 * catalog (`~/.skaile/store/manifests/<slug>.yaml`).
 *
 * Idempotent: when the target file already exists it is left untouched
 * (`wrote: false`) unless `force` is set — a rebuild is a future phase.
 */
export function writeCuratedManifest(input: WriteCuratedManifestInput): WriteCuratedManifestResult {
  const manifestPath = input.manifestFile;
  if (fs.existsSync(manifestPath) && !input.force) {
    return { wrote: false, manifestPath, assetCount: 0 };
  }

  const publisher = input.publisher.startsWith("@") ? input.publisher.slice(1) : input.publisher;
  const inheritPublisher = input.inheritPublisher ?? false;
  const assets = input.assets
    .filter((a) => a.kind !== "preset")
    .map((a) => discoveredToAssetEntry(a, publisher, inheritPublisher))
    .sort((a, b) => `${a.kind}:${a.name}`.localeCompare(`${b.kind}:${b.name}`));

  const manifest: SkPublishManifest = { publisher, assets };
  fs.mkdirSync(path.dirname(manifestPath), { recursive: true });
  fs.writeFileSync(manifestPath, encodeSkaileManifest(manifest), "utf-8");

  return { wrote: true, manifestPath, assetCount: assets.length };
}
